import { getRevenueCatSubscriptionStatus } from '@easy-expense/auth-client';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

const APP_STORE_SUBS_URL = 'https://apps.apple.com/account/subscriptions';
const PLAY_STORE_SUBS_URL = 'https://play.google.com/store/account/subscriptions';
const DISMISSED_KEY = 'ee.billing.mobile-sub-cancellation-notice-dismissed';

export const PlatformSubscriptionNote: React.FC = () => {
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.localStorage.getItem(DISMISSED_KEY) === 'true';
  });
  const [hasMobileSubscription, setHasMobileSubscription] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getRevenueCatSubscriptionStatus()()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setHasMobileSubscription(!!response?.data?.hasActiveSubscription);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.warn('[PlatformSubscriptionNote] Failed to check RevenueCat sub:', error);
        setHasMobileSubscription(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (dismissed || !hasMobileSubscription) {
    return null;
  }

  const onDismiss = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISSED_KEY, 'true');
    }
    setDismissed(true);
  };

  return (
    <Layout.Row
      align
      bg="brandPrimaryXLight"
      border={[1, 'solid', 'inputBorder']}
      radius={12}
      py={12}
      px={16}
      style={{ gap: 12 }}
    >
      <Icon name="information-circle-outline" size={18} color={theme.colors.brandPrimary} />
      <Layout.Column grow>
        <OpenSans.Primary weight="bold-700" size="s-16" color="primary">
          {getTranslation("You're also subscribed on iOS or Android")}
        </OpenSans.Primary>
        <Spacer.Vertical size={2} />
        <OpenSans.Primary size="xs-12" color="secondary">
          {getTranslation(
            'To avoid being charged twice, cancel your existing subscription through the Apple App Store or Google Play. Your data and Easy Expense access stay intact.',
          )}
        </OpenSans.Primary>
        <Spacer.Vertical size={6} />
        <Layout.Row style={{ gap: 16 }}>
          <Layout.PressableRow onClick={() => window.open(APP_STORE_SUBS_URL, '_blank')?.focus()}>
            <OpenSans.Primary size="xs-12" weight="bold-700" color="brandPrimary">
              {getTranslation('Apple App Store')}
            </OpenSans.Primary>
          </Layout.PressableRow>
          <Layout.PressableRow onClick={() => window.open(PLAY_STORE_SUBS_URL, '_blank')?.focus()}>
            <OpenSans.Primary size="xs-12" weight="bold-700" color="brandPrimary">
              {getTranslation('Google Play')}
            </OpenSans.Primary>
          </Layout.PressableRow>
        </Layout.Row>
      </Layout.Column>
      <Layout.PressableColumn center onClick={onDismiss}>
        <Icon name="close" size={18} color={theme.colors.secondary} />
      </Layout.PressableColumn>
    </Layout.Row>
  );
};
